import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {
  clearDraft,
  draftKey,
  getDraft,
  setDraft,
  type MessageDraftKeyParts,
} from '@/utils/message-drafts/messageDrafts';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';

const AUTOSAVE_DEBOUNCE_MS = 300;

type UseMessageDraftArgs = {
  brandId: string;
  conversationId: string;
  campaignId?: string;
};

function toDraftIdentity({
  dashboardUserId,
  brandId,
  conversationId,
  campaignId,
}: UseMessageDraftArgs & {
  dashboardUserId: string;
}): MessageDraftKeyParts | null {
  if (!dashboardUserId || !brandId || !conversationId) {
    return null;
  }
  return {dashboardUserId, brandId, conversationId, campaignId};
}

function useMessageDraft({
  brandId,
  conversationId,
  campaignId,
}: UseMessageDraftArgs) {
  const {dashboardUser} = useDashboardUserQuery({});
  const dashboardUserId = dashboardUser.id ?? '';

  const draftStorageKey =
    dashboardUserId && brandId && conversationId
      ? draftKey({dashboardUserId, brandId, conversationId, campaignId})
      : null;

  const [composedMessage, setComposedMessage] = useState('');
  const composedRef = useRef(composedMessage);
  composedRef.current = composedMessage;

  const draftIdentityRef = useRef<MessageDraftKeyParts | null>(null);
  const draftStorageKeyRef = useRef<string | null>(null);

  // Flush previous thread, then hydrate the next one when identity changes.
  useLayoutEffect(() => {
    const nextIdentity = toDraftIdentity({
      dashboardUserId,
      brandId,
      conversationId,
      campaignId,
    });
    const previousIdentity = draftIdentityRef.current;
    const previousStorageKey = draftStorageKeyRef.current;
    if (
      previousIdentity &&
      previousStorageKey &&
      previousStorageKey !== draftStorageKey
    ) {
      setDraft(previousIdentity, composedRef.current);
    }

    draftIdentityRef.current = nextIdentity;
    draftStorageKeyRef.current = draftStorageKey;
    setComposedMessage(nextIdentity ? (getDraft(nextIdentity) ?? '') : '');
  }, [dashboardUserId, brandId, conversationId, campaignId, draftStorageKey]);

  // Debounced autosave for the active thread.
  useEffect(() => {
    const identity = toDraftIdentity({
      dashboardUserId,
      brandId,
      conversationId,
      campaignId,
    });
    if (!identity) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setDraft(identity, composedMessage);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [composedMessage, dashboardUserId, brandId, conversationId, campaignId]);

  // Flush on unmount so the last keystrokes survive panel close.
  useEffect(
    () => () => {
      if (draftIdentityRef.current) {
        setDraft(draftIdentityRef.current, composedRef.current);
      }
    },
    []
  );

  const flush = () => {
    if (draftIdentityRef.current) {
      setDraft(draftIdentityRef.current, composedRef.current);
    }
  };

  const clearIdentity = (
    identity: MessageDraftKeyParts | null,
    storageKey: string | null
  ) => {
    if (identity) {
      clearDraft(identity);
    }
    // Only wipe the visible composer when we're still on the cleared thread.
    // A send that resolves after the user switched threads must not blank the
    // new composer or delete its draft.
    if (draftStorageKeyRef.current === storageKey) {
      setComposedMessage('');
    }
  };

  const clear = () => {
    clearIdentity(draftIdentityRef.current, draftStorageKeyRef.current);
  };

  /** Snapshot the active identity for a later clear (e.g. send onSuccess). */
  const bindClear = () => {
    const identity = draftIdentityRef.current
      ? {...draftIdentityRef.current}
      : null;
    const storageKey = draftStorageKeyRef.current;
    return () => clearIdentity(identity, storageKey);
  };

  return {
    composedMessage,
    setComposedMessage,
    flush,
    clear,
    bindClear,
  };
}

export default useMessageDraft;
